import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import HeroSection from '../components/Hero/HeroSection';
import HowItWorks from '../components/HowItWorks';
import GameDemo from '../components/GameDemo';
import GamifiedButton from '../components/common/GamifiedButton';
import { WavyDivider } from '../components/effects';
import { useCart } from '../contexts/CartContext';
import ReviewsSection from '../components/Reviews/ReviewsSection';
import { getProducts, getReviews } from '../services/api';
import { packPhoto } from '../lib/packPhotos';
import { useLang } from '../i18n/LanguageContext';
import { formaterPrix } from '../lib/prix';

const packColorClasses = {
  pink: 'border-[#F4A6C1] bg-gradient-to-br from-[#F4A6C1]/8 to-white',
  mint: 'border-[#A8D5BA] bg-gradient-to-br from-[#A8D5BA]/8 to-white',
  sky: 'border-[#A7D8F7] bg-gradient-to-br from-[#A7D8F7]/8 to-white',
  warm: 'border-[#4AA294] bg-gradient-to-br from-[#4AA294]/8 to-white',
  purple: 'border-[#A78BFA] bg-gradient-to-br from-[#A78BFA]/8 to-white',
};

export default function Home() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { lang, t, tp, tpList } = useLang();

  // Constante migrée dans le composant : elle contient du texte traduit et
  // t() est un hook. Le nom JOYFUL REVEAL est la marque, il ne se traduit pas.
  const PRODUCT = {
    id: 1,
    name: 'JOYFUL REVEAL',
    image: '/Vignette article joyfull reveal.png',
    imageSrcSet: null,
    description: 'Come and discover this game and have an unforgettable time!',
    description_long: t('home.vitrine.description'),
  };

  const [selectedPack, setSelectedPack] = useState('premium');
  const [selectedGender, setSelectedGender] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sert uniquement au balisage aggregateRating : c'est ce qui permet à Google
  // d'afficher les étoiles dans ses résultats.
  const [noteGlobale, setNoteGlobale] = useState(null);

  // Handle hash scroll (e.g., /#reviews)
  useEffect(() => {
    if (!loading && window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [loading]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error('Failed to load products', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Note moyenne réelle, pour le balisage Schema.org. Silencieux en cas
  // d'échec : l'absence d'étoiles ne doit jamais casser la page.
  useEffect(() => {
    getReviews(PRODUCT.id)
      .then((data) => {
        const total = Number(data?.totalReviews) || 0;
        const moyenne = Number(data?.averageRating);
        if (total > 0 && moyenne > 0) setNoteGlobale({ total, moyenne });
      })
      .catch(() => {});
  }, []);

  // Map database products to PACKS format
  const PACKS = products.map(product => ({
    id: product.id,
    name: product.name,
    tagline: product.tagline || '',
    price: Number(product.price),
    original_price: product.originalPrice ? Number(product.originalPrice) : null,
    description: product.description || '',
    // Garde indispensable : ce tableau est rendu par .map() plus bas, et
    // l'ErrorBoundary est monté à la racine — une valeur non-tableau ferait
    // tomber TOUT le site, navbar et pied de page compris.
    features: Array.isArray(product.features) ? product.features : [],
    highlight: product.highlight || false,
    badge: product.badge || '',
    color: product.color || 'pink',
    variantType: product.variantType,
    weight: product.weight
  }));

  // Schema.org JSON-LD. On n'injecte le bloc Product qu'une fois les packs
  // chargés : un Product avec une liste d'offres vide est refusé par Google.
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'OnlineStore',
      'name': 'Mirum Games',
      'description':
        'Joyful Reveal, le jeu de gender reveal party : quiz en équipes, puzzle à assembler et pièce finale à gratter qui révèle le sexe de bébé.',
      'url': 'https://www.mirumgames.com',
      'logo': 'https://www.mirumgames.com/logo.webp',
      'image': 'https://www.mirumgames.com/boy-with-bear.png',
      'sameAs': [
        'https://www.instagram.com/mirumgames/',
        'https://www.facebook.com/people/MirumGames/61553240464727/'
      ]
    };

    if (PACKS.length > 0) {
      const prices = PACKS.map(pack => Number(pack.price)).filter(p => !Number.isNaN(p));
      schema.mainEntity = {
        '@type': 'Product',
        'name': 'Joyful Reveal - Ours',
        'description':
          'Jeu de gender reveal : 2 équipes, des questions amusantes, 11 pièces de puzzle à gagner et une pièce finale à gratter qui révèle le sexe de bébé.',
        'image': 'https://www.mirumgames.com/boy-with-bear.png',
        'brand': {
          '@type': 'Brand',
          'name': 'Mirum Games'
        },
        'offers': {
          '@type': 'AggregateOffer',
          'priceCurrency': 'EUR',
          'lowPrice': Math.min(...prices).toFixed(2),
          'highPrice': Math.max(...prices).toFixed(2),
          'offerCount': PACKS.length,
          'availability': 'https://schema.org/InStock',
          'url': 'https://www.mirumgames.com/shop'
        }
      };

      // Étoiles dans les résultats Google. Uniquement des avis réels et
      // approuvés — on ne publie jamais de note inventée.
      if (noteGlobale) {
        schema.mainEntity.aggregateRating = {
          '@type': 'AggregateRating',
          'ratingValue': noteGlobale.moyenne.toFixed(1),
          'reviewCount': noteGlobale.total,
          'bestRating': '5',
          'worstRating': '1'
        };
      }
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => script.remove();
  }, [products, noteGlobale]);

  const handleAddToCart = () => {
    const pack = PACKS.find(p => p.id === selectedPack);

    // Skip gender check for mixed, boy, or girl variant types
    const needsGenderSelection = !pack?.variantType || pack.variantType === 'standard';

    if (needsGenderSelection && !selectedGender) {
      // Shake animation or alert
      const genderSection = document.getElementById('gender-selection');
      genderSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      genderSection?.classList.add('animate-shake');
      setTimeout(() => genderSection?.classList.remove('animate-shake'), 500);
      return;
    }
    if (pack) {
      // Use variantType for logic
      const isMixed = pack.variantType === 'mixed';
      const isBoy = pack.variantType === 'boy';
      const isGirl = pack.variantType === 'girl';

      let variantLabel = '';
      if (isMixed) variantLabel = t('home.variante.mixte');
      else if (isBoy) variantLabel = t('home.variante.garcon');
      else if (isGirl) variantLabel = t('home.variante.fille');
      else variantLabel = selectedGender === 'boy' ? t('home.variante.garcon') : t('home.variante.fille');

      // Don't append variant label if it's already in the name (for mixed pack)
      const finalName = isMixed
        ? `${PRODUCT.name} - ${tp(pack, 'name', pack.name)}`
        : `${PRODUCT.name} - ${tp(pack, 'name', pack.name)} (${variantLabel})`;

      addToCart({
        // L'id doit être celui du pack en base ('flash', 'premium'…). Il partait
        // avant sur PRODUCT.id, une constante à 1 qui n'existe dans aucun
        // catalogue : le backend faisait Product.find(1) → introuvable → 400
        // « Certains produits ne sont plus disponibles », et le paiement était
        // impossible pour tout panier créé depuis l'accueil.
        id: pack.id,
        name: finalName,
        price: pack.price,
        image: packPhoto(pack),
        weight: pack.weight || 1.5, // Use DB weight, fallback only if missing
        variant: isMixed ? 'mixed' : (isBoy ? 'boy' : (isGirl ? 'girl' : selectedGender)),
        language: 'FR' // Default language
      });
      navigate('/cart');
    }
  };

  // Le pack réellement sélectionné, une fois le catalogue chargé.
  const packChoisi = PACKS.find(p => p.id === selectedPack) || null;

  const handleDiscoverClick = () => {
    document.querySelector('.showcase-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('home.etat.chargement')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <SEO
        title={t('home.seo.titre')}
        description={t('home.seo.description')}
        url="/"
        keywords="gender reveal, jeu gender reveal, puzzle révélation sexe bébé, gender reveal party, idée gender reveal, cadeau grossesse"
      />

      {/* Hero Section */}
      <HeroSection onDiscoverClick={handleDiscoverClick} />

      {/* Wavy transition */}
      <WavyDivider color="#F7F4EE" height={100} animate={true} />

      {/* On explique le jeu AVANT d'annoncer un prix : le visiteur ne connaît pas
          encore le concept. Le bouton « Découvrez notre offre » du hero saute
          directement à .showcase-section pour ceux qui viennent acheter. */}
      <HowItWorks />

      {/* La partie express jouable — le moment fort du produit, à vivre plutôt
          qu'à lire. Remplace la vidéo qu'on n'a pas. */}
      <GameDemo />

      {/* Showcase Section */}
      <section className="showcase-section py-20 px-5 bg-gradient-to-br from-[#F7F4EE] via-[#F3F2EA] to-[#F7F4EC]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* Product Image */}
          <div className="relative flex items-center justify-center animate-[fadeIn_1s_ease-out] order-first lg:order-none overflow-hidden">
            <img
              src={PRODUCT.image}
              srcSet={PRODUCT.imageSrcSet}
              sizes="(max-width: 768px) 600px, 600px"
              alt={t('home.vitrine.altProduit')}
              className="max-w-full h-auto rounded-2xl shadow-[0_30px_60px_rgba(93,78,55,0.2)] animate-[floatGentle_3s_ease-in-out_infinite]"
              width="609"
              height="609"
              loading="lazy"
            />
            <div className="absolute w-[130%] h-[130%] bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10 rounded-2xl -z-10 border-4 border-dashed border-[#4AA294]"></div>
          </div>

          {/* Product Info & Packs */}
          <div className="flex flex-col gap-10 animate-[fadeInUp_1s_ease-out]">

            {/* Header */}
            <div className="flex flex-col gap-4">
              <span className="inline-flex items-center gap-2 bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wide w-fit">
                {t('home.vitrine.badge')}
              </span>
              <h1 className="m-0 text-3xl sm:text-4xl md:text-5xl font-black text-[#33403B] uppercase tracking-tight break-words">
                {PRODUCT.name}
              </h1>
              <p className="m-0 text-base leading-relaxed text-[#33403B]">
                {PRODUCT.description_long}
              </p>
            </div>

            {/* Packs Selection */}
            <div className="flex flex-col gap-6">
              <h2 className="m-0 text-xl font-bold text-[#33403B] uppercase">
                {t('home.packs.titre')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PACKS.map(pack => (
                  <div
                    key={pack.id}
                    onClick={() => setSelectedPack(pack.id)}
                    className={`relative bg-gradient-to-br from-white to-[#F7F4EC] border-3 rounded-lg p-6 cursor-pointer transition-all duration-300 shadow-md flex flex-col gap-3 ${packColorClasses[pack.color]
                      } ${selectedPack === pack.id
                        ? 'border-[4px] shadow-[0_0_25px_rgba(139,111,71,0.35)]'
                        : ''
                      } ${pack.highlight
                        ? 'border-[4px] shadow-[0_0_20px_rgba(139,111,71,0.25)] scale-[1.02] hover:scale-105'
                        : 'hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(139,111,71,0.2)]'
                      }`}
                  >
                    {/* Badge - Only show if there's a promo */}
                    {pack.original_price && pack.original_price > pack.price && (
                      <div className="absolute top-3 right-3 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wide">
                        -{Math.round(((pack.original_price - pack.price) / pack.original_price) * 100)}%
                      </div>
                    )}

                    {/* Star (highlight) */}
                    {pack.highlight && (
                      <div className="absolute -top-4 right-5 text-[32px] animate-[floatGentle_2s_ease-in-out_infinite]">
                        ⭐
                      </div>
                    )}

                    {/* Content */}
                    <div className="text-xs font-semibold text-[#2F7E72] uppercase tracking-wide">
                      {tp(pack, 'tagline', pack.tagline)}
                    </div>
                    <h3 className="m-0 text-base font-bold text-[#33403B]">
                      {tp(pack, 'name', pack.name)}
                    </h3>

                    {/* Price */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {pack.original_price && (
                        <span className="text-[13px] text-[#6C7B75] line-through">
                          {formaterPrix(pack.original_price, lang)}
                        </span>
                      )}
                      <div className="text-2xl font-black text-[#2F7E72]">
                        {formaterPrix(pack.price, lang)}
                      </div>
                      {pack.discount && (
                        <span className="bg-[#C44569] text-white px-2 py-1 rounded text-[11px] font-bold">
                          {t('home.packs.economisez', { pct: pack.discount })}
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="m-0 text-[13px] text-[#33403B] leading-relaxed">
                      {tp(pack, 'description', pack.description).length > 100
                        ? tp(pack, 'description', pack.description).substring(0, 100) + '...'
                        : tp(pack, 'description', pack.description)}
                    </p>

                    {/* Features */}
                    <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
                      {tpList(pack, 'features', pack.features).map((feature, idx) => (
                        <li key={idx} className="text-xs text-[#33403B] font-medium">
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Select Button */}
                    <button className={`border-none px-4 py-3 rounded text-[13px] font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 mt-2 ${selectedPack === pack.id
                      ? 'bg-gradient-to-br from-[#A8D5BA] to-[#7FBE9E] text-white'
                      : 'bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white hover:shadow-[0_6px_15px_rgba(139,111,71,0.3)] hover:-translate-y-0.5'
                      }`}>
                      {selectedPack === pack.id ? t('home.packs.selectionne') : t('home.packs.selectionner')}
                    </button>

                    {/* Sortie vers la fiche produit. La carte ne montre que 100
                        caractères de description : celui qui hésite doit pouvoir
                        aller lire le détail sans repasser par la boutique.
                        stopPropagation, sinon le clic sélectionne aussi le pack. */}
                    <Link
                      to={`/product/${pack.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-cta={`home:fiche-${pack.id}`}
                      className="mt-1 block text-center text-[12px] font-semibold text-[#2F7E72] underline decoration-[#4AA294]/40 underline-offset-4 hover:text-[#17453E]"
                    >
                      {t('home.packs.voirFiche')}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Pack Summary */}
            {selectedPack && (
              <div className="bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10 border-2 border-[#4AA294] rounded-lg p-5 flex flex-col md:flex-row justify-between items-center gap-4 animate-[fadeInUp_0.5s_ease-out]">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold text-[#2F7E72] uppercase tracking-wide">
                    {t('home.recap.packChoisi')}
                  </span>
                  <span className="text-base font-bold text-[#33403B]">
                    {tp(packChoisi, 'name', packChoisi?.name)}
                  </span>

                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-bold text-[#2F7E72] uppercase tracking-wide">
                    {t('home.recap.prixTotal')}
                  </span>
                  <span className="text-xl font-bold text-[#4AA294]">
                    {formaterPrix(PACKS.find(p => p.id === selectedPack)?.price, lang)}
                  </span>
                </div>
              </div>
            )}

            {/* Gender Selection */}
            <div id="gender-selection" className="flex flex-col gap-4 mt-6 animate-[fadeInUp_0.6s_ease-out]">
              <h3 className="m-0 text-lg font-bold text-[#33403B] uppercase">
                {PACKS.find(p => p.id === selectedPack)?.variantType === 'standard' ? t('home.genre.cestPour') : t('home.genre.contenuPack')}
              </h3>

              {/* Gender Selection - Only show if variantType is standard */}
              {PACKS.find(p => p.id === selectedPack)?.variantType === 'standard' || !PACKS.find(p => p.id === selectedPack)?.variantType ? (
                <div className="flex gap-3 mb-6 animate-[fadeIn_0.3s_ease-out]">
                  <button
                    onClick={() => setSelectedGender('boy')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-center gap-2 ${selectedGender === 'boy'
                      ? 'border-blue-400 bg-blue-50 text-blue-700 shadow-md scale-[1.02]'
                      : 'border-gray-200 text-gray-500 hover:border-blue-200 hover:bg-blue-50/50'
                      }`}
                  >
                    <span className="text-xl">👦</span>
                    <span className="font-bold text-sm uppercase tracking-wide">{t('home.variante.garcon')}</span>
                  </button>
                  <button
                    onClick={() => setSelectedGender('girl')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-center gap-2 ${selectedGender === 'girl'
                      ? 'border-pink-400 bg-pink-50 text-pink-700 shadow-md scale-[1.02]'
                      : 'border-gray-200 text-gray-500 hover:border-pink-200 hover:bg-pink-50/50'
                      }`}
                  >
                    <span className="text-xl">👧</span>
                    <span className="font-bold text-sm uppercase tracking-wide">{t('home.variante.fille')}</span>
                  </button>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100 text-center animate-[fadeIn_0.3s_ease-out]">
                  <span className="text-2xl mb-2 block">
                    {PACKS.find(p => p.id === selectedPack)?.variantType === 'mixed' ? '👫' : (PACKS.find(p => p.id === selectedPack)?.variantType === 'boy' ? '👦' : '👧')}
                  </span>
                  <p className="text-sm font-bold text-purple-900 m-0">
                    {PACKS.find(p => p.id === selectedPack)?.variantType === 'mixed'
                      ? t('home.genre.packMixte')
                      : (PACKS.find(p => p.id === selectedPack)?.variantType === 'boy'
                        ? t('home.genre.versionGarcon')
                        : t('home.genre.versionFille'))}
                  </p>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <div className="flex flex-col gap-3 mt-4">
              <GamifiedButton
                variant="primary"
                size="lg"
                onClick={handleAddToCart}
                data-cta="home:add-to-cart"
              >
                {t('home.cta.ajouterPanier')}
              </GamifiedButton>

              {/* Deuxième porte, pour celui qui n'est pas encore prêt à acheter :
                  photos, contenu détaillé, fiche technique et règles. Sans elle,
                  l'accueil n'offre que « acheter » ou « partir ». On n'affiche le
                  lien que si le pack est bien chargé : sinon le libellé finirait
                  sur un « du » orphelin. */}
              {packChoisi && (
                <Link
                  to={`/product/${packChoisi.id}`}
                  data-cta="home:fiche-pack-selectionne"
                  className="block rounded-lg border-2 border-[#4AA294]/50 bg-white/60 px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide text-[#2F7E72] no-underline transition-all hover:-translate-y-0.5 hover:border-[#2F7E72] hover:bg-[#4AA294]/10"
                >
                  {t('home.cta.voirFichePack', { pack: tp(packChoisi, 'name', packChoisi.name) })}
                </Link>
              )}

              <p className="m-0 text-[13px] text-[#2F7E72] text-center italic">
                {t('home.cta.magique')}
              </p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-5 border-t-2 border-[#2F7E72]/10">
              {[
                { icon: '🎉', text: t('home.badges.cleEnMain') },
                { icon: '💝', text: t('home.badges.pretAOffrir') },
                { icon: '📦', text: t('home.badges.livraisonRapide') },
              ].map((badge, i) => (
                <div key={i} className="flex flex-col items-center gap-2 text-center">
                  <span className="text-[28px] inline-block">{badge.icon}</span>
                  <span className="text-xs font-semibold text-[#33403B] uppercase tracking-wide">
                    {badge.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection productId={PRODUCT.id} />

      {/* NOTE : l'ancienne section « Pourquoi Joyful Reveal ? » (4 cartes emoji)
          a été retirée — ses trois premiers arguments sont désormais racontés en
          photos par <HowItWorks />, et « Emballage cadeau » est repris dans les
          badges de confiance ci-dessus. */}

      {/* Introduction Section - New Concept */}
      <section className="pt-20 pb-20 px-5 bg-gradient-to-br from-white via-[#F7F4EC] to-[#F3F2EA]">
        <div className="max-w-[900px] mx-auto">
          <div className="bg-white rounded-3xl border-3 border-[#4AA294] p-8 md:p-12 shadow-[0_20px_60px_rgba(139,111,71,0.15)]">

            {/* Badge */}
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center gap-2 bg-gradient-to-br from-[#F4A6C1]/20 to-[#A8D5BA]/20 border-2 border-[#F4A6C1] text-[#17453E] px-5 py-3 rounded-2xl text-sm font-bold uppercase tracking-wide">
                {t('home.concept.badge')}
              </span>
            </div>

            {/* Content */}
            <div className="space-y-6 text-[#33403B]">

              {/* Paragraph 1 */}
              <div className="text-center">
                <p className="text-lg md:text-xl leading-relaxed m-0">
                  <strong className="text-[#2F7E72]">{t('home.concept.question')}</strong> {t('home.concept.questionSuite')}
                </p>
                <p className="text-lg md:text-xl leading-relaxed mt-4 m-0 font-semibold text-[#2F7E72]">
                  {t('home.concept.annonce')}
                </p>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#4AA294] to-transparent"></div>
                <span className="text-3xl">🧩</span>
                <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#4AA294] to-transparent"></div>
              </div>

              {/* Paragraph 2 */}
              <p className="text-base md:text-lg leading-relaxed text-center md:text-left m-0">
                {t('home.concept.puzzleAvant')} <strong className="text-[#2F7E72]">{t('home.concept.puzzleMot')}</strong> {t('home.concept.puzzleApres')}
              </p>

              {/* Paragraph 3 */}
              <p className="text-base md:text-lg leading-relaxed text-center md:text-left m-0">
                {t('home.concept.experienceAvant')} <strong className="text-[#2F7E72]">{t('home.concept.experienceMot')}</strong> {t('home.concept.experienceApres')}
              </p>

              {/* Highlight Box */}
              <div className="bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10 border-2 border-[#A8D5BA] rounded-2xl p-6 mt-8">
                <p className="text-base md:text-lg leading-relaxed text-center m-0">
                  <span className="text-2xl mr-2">💝</span>
                  {t('home.concept.cadeauAvant')} <strong className="text-[#2F7E72]">{t('home.concept.cadeauMot')}</strong> {t('home.concept.cadeauApres')}
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* Instagram Reel Section */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-[#FEFEFA] via-[#F3F2EA] to-[#F7F4EE] border-t-4 border-[#4AA294]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wide mb-4">
              {t('home.instagram.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-[#33403B] uppercase tracking-tight mb-4">
              {t('home.instagram.titre')}
            </h2>
            <p className="text-lg text-[#2F7E72] max-w-2xl mx-auto">
              {t('home.instagram.sousTitre')}
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            {/* Instagram Preview Card */}
            <div className="w-full md:w-auto flex justify-center">
              <a
                href="https://www.instagram.com/reel/DRg9U4NDrJp/"
                target="_blank"
                rel="noopener noreferrer"
                data-cta="home:instagram-reel"
                className="group block w-full max-w-sm"
              >
                <div className="relative bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#FD1D1D] p-1 rounded-2xl shadow-[0_20px_60px_rgba(139,111,71,0.3)] transition-all duration-300 hover:shadow-[0_25px_70px_rgba(139,111,71,0.4)] hover:-translate-y-2">
                  <div className="bg-white rounded-xl overflow-hidden">
                    {/* Visuel du post. Une vraie image : le placeholder gris
                        « Cliquez pour voir » donnait l'impression d'un bloc cassé. */}
                    <div className="aspect-[4/5] relative overflow-hidden bg-[#F3F2EA]">
                      <img
                        src="/parents-reveal-color.webp"
                        srcSet="/parents-reveal-color-570.webp 570w, /parents-reveal-color.webp 1000w"
                        sizes="(max-width: 768px) 90vw, 384px"
                        alt={t('home.instagram.altReel')}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/5">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="#E1306C" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Instagram-style footer */}
                    <div className="p-3 bg-white border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#FD1D1D] flex items-center justify-center flex-shrink-0">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057-1.645.069-4.849.069-3.205 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-xs text-gray-900">@mirumgames</p>
                          <p className="text-[10px] text-gray-500">{t('home.instagram.voirSur')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </div>

            {/* Text Content */}
            <div className="w-full md:w-1/2 space-y-6">
              <div className="bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10 border-2 border-[#A8D5BA] rounded-2xl p-6">
                <h3 className="text-2xl font-bold text-[#33403B] mb-4 flex items-center gap-3">
                  <span className="text-3xl">✨</span>
                  {t('home.instagram.momentTitre')}
                </h3>
                <p className="text-base text-[#33403B] leading-relaxed">
                  {t('home.instagram.momentTexte')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-[#F7F4EE] to-[#F3F2EA] border-2 border-[#4AA294] rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">🎮</div>
                  <p className="text-sm font-bold text-[#33403B]">{t('home.instagram.jeuInteractif')}</p>
                </div>
                <div className="bg-gradient-to-br from-[#F7F4EE] to-[#F3F2EA] border-2 border-[#4AA294] rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">👨‍👩‍👧‍👦</div>
                  <p className="text-sm font-bold text-[#33403B]">{t('home.instagram.enFamille')}</p>
                </div>
              </div>

              <a
                href="https://www.instagram.com/mirumgames/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:from-[#2F7E72] hover:to-[#4AA294] no-underline"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057-1.645.069-4.849.069-3.205 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z" />
                </svg>
                {t('home.instagram.suivezNous')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
